import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Briefcase, 
  Award, 
  BookOpen, 
  Download, 
  ArrowRight, 
  Clock,
  User,
  Anchor,
  Pipette as Pipeline,
  Shield,
  Lightbulb
} from 'lucide-react';

const Resources = () => {
  const { t } = useTranslation();

  // Resource categories with items
  const resourceCategories = [
    {
      title: t('resources.categories.company.title'),
      subtitle: t('resources.categories.company.subtitle'),
      icon: Briefcase,
      color: 'blue',
      items: [
        {
          icon: User,
          title: t('resources.items.executive.title'),
          desc: t('resources.items.executive.desc'),
          action: t('resources.items.executive.action'),
          link: '/leadership',
          available: true,
          featured: true
        },
        {
          icon: FileText,
          title: t('resources.items.companyProfile.title'),
          desc: t('resources.items.companyProfile.desc'),
          action: 'Download Profile',
          link: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/jfzepw1x_Seli%20Marine%20Company%20Profile.pdf',
          available: true,
          isExternal: true
        },
        {
          icon: Award,
          title: t('resources.items.capabilities.title'),
          desc: t('resources.items.capabilities.desc'),
          action: t('resources.items.capabilities.action'),
          link: '/contact?subject=Capability Statement',
          available: false
        }
      ]
    },
    {
      title: t('resources.categories.projects.title'),
      subtitle: t('resources.categories.projects.subtitle'),
      icon: Anchor,
      color: 'amber',
      items: [
        {
          icon: Pipeline,
          title: t('resources.items.senegal.title'),
          desc: t('resources.items.senegal.desc'),
          action: t('resources.items.senegal.action'),
          link: '/contact?subject=Senegal LNG Case Study',
          available: false,
          highlighted: true
        },
        {
          icon: FileText,
          title: t('resources.items.portfolio.title'),
          desc: t('resources.items.portfolio.desc'),
          action: t('resources.items.portfolio.action'),
          link: '/contact?subject=Project Portfolio',
          available: false
        }
      ]
    },
    {
      title: t('resources.categories.technical.title'),
      subtitle: t('resources.categories.technical.subtitle'),
      icon: Shield,
      color: 'cyan',
      items: [
        {
          icon: BookOpen,
          title: t('resources.items.serviceCatalogue.title'),
          desc: t('resources.items.serviceCatalogue.desc'),
          action: t('resources.items.serviceCatalogue.action'),
          link: '/contact?subject=Service Catalogue',
          available: false
        },
        {
          icon: Shield,
          title: t('resources.items.hseQuality.title'),
          desc: t('resources.items.hseQuality.desc'),
          action: t('resources.items.hseQuality.action'),
          link: '/contact?subject=HSE Quality Documentation',
          available: false
        }
      ]
    },
    {
      title: t('resources.categories.insights.title'),
      subtitle: t('resources.categories.insights.subtitle'),
      icon: Lightbulb,
      color: 'purple',
      items: [
        {
          icon: BookOpen,
          title: t('resources.items.insights.title'),
          desc: t('resources.items.insights.desc'),
          action: t('resources.items.insights.action'),
          link: null,
          available: false,
          comingSoon: true
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="resources-title">
              {t('resources.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('resources.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources by Category */}
      {resourceCategories.map((category, catIndex) => {
        const CategoryIcon = category.icon;
        const isAlternate = catIndex % 2 === 1;
        
        return (
          <section 
            key={catIndex}
            className={`py-16 ${isAlternate ? 'bg-slate-50' : 'bg-white'}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Category Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-start space-x-4 mb-10"
              >
                <div className={`p-4 rounded-xl ${
                  category.color === 'blue' ? 'bg-blue-900' :
                  category.color === 'amber' ? 'bg-amber-500' :
                  category.color === 'cyan' ? 'bg-cyan-700' :
                  'bg-purple-700'
                }`}>
                  <CategoryIcon className="text-white" size={28} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-1">
                    {category.title}
                  </h2>
                  <p className="text-gray-600">
                    {category.subtitle}
                  </p>
                </div>
              </motion.div>

              {/* Resource Cards */}
              <div className={`grid gap-6 ${
                category.items.length === 1 ? 'grid-cols-1' :
                category.items.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {category.items.map((item, itemIndex) => {
                  const ItemIcon = item.icon;
                  
                  return (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: itemIndex * 0.1 }}
                      viewport={{ once: true }}
                      className={`relative rounded-xl shadow-lg hover:shadow-2xl transition-all overflow-hidden group ${
                        item.featured 
                          ? 'bg-gradient-to-br from-blue-900 to-slate-900 text-white ring-2 ring-amber-400' 
                          : 'bg-white'
                      }`}
                      data-testid={`resource-card-${itemIndex}`}
                    >
                      {/* Status Badge */}
                      <div className="absolute top-4 right-4">
                        {item.available ? (
                          <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                            {t('resources.available')}
                          </span>
                        ) : item.comingSoon ? (
                          <span className="px-3 py-1 bg-gray-400 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                            <Clock size={12} />
                            <span>{t('resources.comingSoon')}</span>
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">
                            On Request
                          </span>
                        )}
                      </div>

                      <div className="p-6 pr-32">
                        {/* Icon */}
                        <div className={`inline-flex p-3 rounded-lg mb-4 ${
                          item.featured 
                            ? 'bg-amber-500' 
                            : item.highlighted
                            ? 'bg-amber-100'
                            : 'bg-blue-50'
                        }`}>
                          <ItemIcon className={
                            item.featured 
                              ? 'text-white' 
                              : item.highlighted
                              ? 'text-amber-700'
                              : 'text-blue-900'
                          } size={24} />
                        </div>

                        {/* Title */}
                        <h3 className={`text-xl font-bold mb-2 ${
                          item.featured ? 'text-white' : 'text-slate-900'
                        }`}>
                          {item.title}
                        </h3>

                        {/* Description */}
                        <p className={`text-sm mb-6 ${
                          item.featured ? 'text-blue-100' : 'text-gray-600'
                        }`}>
                          {item.desc}
                        </p>

                        {/* Action Button */}
                        {item.comingSoon ? (
                          <div className={`inline-flex items-center text-sm font-semibold ${
                            item.featured ? 'text-amber-400' : 'text-gray-400'
                          }`}>
                            <Clock className="mr-2" size={16} />
                            {item.action}
                          </div>
                        ) : item.link ? (
                          item.isExternal ? (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className={`inline-flex items-center text-sm font-semibold transition-colors ${
                                item.featured 
                                  ? 'text-amber-400 hover:text-amber-300' 
                                  : item.available
                                  ? 'text-green-700 hover:text-green-800'
                                  : 'text-blue-900 hover:text-blue-700'
                              }`}
                              data-testid={`resource-action-${itemIndex}`}
                            >
                              <Download className="mr-2" size={16} />
                              {item.action}
                            </a>
                          ) : (
                            <Link
                              to={item.link}
                              className={`inline-flex items-center text-sm font-semibold transition-colors ${
                                item.featured 
                                  ? 'text-amber-400 hover:text-amber-300' 
                                  : item.available
                                  ? 'text-green-700 hover:text-green-800'
                                  : 'text-blue-900 hover:text-blue-700'
                              }`}
                              data-testid={`resource-action-${itemIndex}`}
                            >
                              {item.available ? <Download className="mr-2" size={16} /> : <ArrowRight className="mr-2" size={16} />}
                              {item.action}
                            </Link>
                          )
                        ) : null}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('resources.cta.title')}
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            {t('resources.cta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg"
            data-testid="resources-cta"
          >
            {t('resources.cta.button')} <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Resources;
